import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, ScrollView, Platform } from 'react-native';
import PlatformIcon from '../common/PlatformIcon';
import { ORANGE_ACTION } from '../../constants/colors';

export interface PoiDetailContentProps {
  onClose?: () => void;
  title: string;
  imageUrl?: string;
  address?: string;
  description?: string;
  metaData?: Array<{ label: string; value: string }>;
  tags?: string[];
  rawData?: Record<string, any>;
}

const PoiDetailContent: React.FC<PoiDetailContentProps> = ({ onClose, title, imageUrl, address, description, metaData = [], tags = [], rawData }) => {
  const metaLabelsLower = new Set((metaData || []).map((m) => (m.label || '').trim().toLowerCase()));
  const hasMetaLabel = (...labels: string[]) => labels.some((l) => metaLabelsLower.has(l.trim().toLowerCase()));

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' ? (
        // web: render a DOM wrapper so we can precisely control image size and overlay
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <div className="cm-poi-header-wrapper">
          {/* image shell centers the image and provides relative positioning for the back button */}
          {/* @ts-ignore */}
          <div className="cm-poi-image-shell">
            {/* @ts-ignore */}
            <img src={imageUrl || ''} alt={title} className="cm-poi-header-img" />
            {onClose ? (
              // @ts-ignore
              <button className="cm-poi-back-btn" onClick={onClose} aria-label="back">
                  {/* use Material arrow icon inside an orange circular container */}
                  {/* @ts-ignore */}
                  <span style={{ display: 'inline-block' }}>
                    {/* Render via PlatformIcon as inline element */}
                    {/* @ts-ignore */}
                    {React.createElement((PlatformIcon as any), { name: 'arrow_back', size: 20, color: '#fff', family: 'MaterialIcons' })}
                  </span>
                </button>
            ) : null}
          </div>
        </div>
      ) : (
        <View style={styles.headerImageWrapper}>
          {imageUrl ? <Image source={{ uri: imageUrl }} style={styles.headerImage} /> : <View style={styles.headerImage} />}
          <View style={styles.headerOverlay} />
          {onClose ? (
            <TouchableOpacity style={[styles.backButton, { backgroundColor: ORANGE_ACTION }]} onPress={onClose} accessibilityLabel="返回">
              <PlatformIcon name="arrow_back" size={22} color="#fff" family="MaterialIcons" />
            </TouchableOpacity>
          ) : null}
        </View>
      )}

            <ScrollView style={[styles.content, Platform.OS === 'web' && styles.webContentContainer]} contentContainerStyle={{ paddingBottom: 40 }}>
        <Text style={styles.title}>{title}</Text>
        {address ? (
          <View style={styles.addressRow}>
            <PlatformIcon name="location_on" size={18} color="#9CA3AF" />
            <Text style={styles.addressText}>{address}</Text>
          </View>
        ) : null}

        {description ? (
          <View style={styles.section}>
            <View style={styles.sectionHeader}><PlatformIcon name="description" size={20} color="#007AFF" /><Text style={styles.sectionHeaderText}>景點介紹</Text></View>
            <Text style={styles.description}>{description}</Text>
          </View>
        ) : null}

        {metaData && metaData.length ? (
          <View style={styles.section}>
            <View style={styles.sectionDivider} />
            <View style={styles.sectionHeader}><PlatformIcon name="schedule" size={20} color="#007AFF" /><Text style={styles.sectionHeaderText}>營業時間</Text></View>
            <View style={styles.metaBlock}>
              {metaData
                .filter((m) => !(m.label && m.label.trim() === '地址'))
                .map((m, i, arr) => (
                  <View key={m.label || String(i)} style={[styles.metaRow, i < arr.length - 1 ? styles.metaRowDivider : null]}>
                    <Text style={styles.metaLabel}>{m.label}</Text>
                    <Text style={styles.metaValue}>{m.value}</Text>
                  </View>
                ))}
            </View>
          </View>
        ) : null}

        {rawData?.rawMergedPayload ? (
          <View style={styles.section}>
            <View style={styles.sectionDivider} />
            <View style={styles.sectionHeader}><PlatformIcon name="info" size={20} color="#007AFF" /><Text style={styles.sectionHeaderText}>其他資訊</Text></View>
            <View style={styles.metaBlock}>
              {rawData.rawMergedPayload.suggested_duration || rawData.rawMergedPayload.suggestedDuration ? (
                !hasMetaLabel('建議時長', '建议时长', 'suggested duration') ? (
                  <View style={styles.metaRow}><Text style={styles.metaLabel}>建議時長</Text><Text style={styles.metaValue}>{String(rawData.rawMergedPayload.suggested_duration || rawData.rawMergedPayload.suggestedDuration)} 分鐘</Text></View>
                ) : null
              ) : null}
              {rawData.rawMergedPayload.cost_level ? (
                !hasMetaLabel('參考價位', '参考价位', 'price', 'cost_level') ? (
                  <View style={styles.metaRow}><Text style={styles.metaLabel}>參考價位</Text><Text style={styles.metaValue}>{String(rawData.rawMergedPayload.cost_level)}</Text></View>
                ) : null
              ) : null}
              {rawData.rawMergedPayload.best_time || rawData.rawMergedPayload.bestTime ? (
                !hasMetaLabel('推薦時段', '推荐时段', 'best time') ? (
                  <View style={styles.metaRow}><Text style={styles.metaLabel}>推薦時段</Text><Text style={styles.metaValue}>{String(rawData.rawMergedPayload.best_time || rawData.rawMergedPayload.bestTime)}</Text></View>
                ) : null
              ) : null}
              {rawData.rawMergedPayload.category || rawData.rawMergedPayload.type ? (
                !hasMetaLabel('分類', '类别', 'category', 'type') ? (
                  <View style={styles.metaRow}><Text style={styles.metaLabel}>分類</Text><Text style={styles.metaValue}>{String(rawData.rawMergedPayload.category || rawData.rawMergedPayload.type)}</Text></View>
                ) : null
              ) : null}
              {rawData.rawMergedPayload.image_gallery && Array.isArray(rawData.rawMergedPayload.image_gallery) && rawData.rawMergedPayload.image_gallery.length ? (
                !hasMetaLabel('圖集', '图集', 'gallery', 'image_gallery') ? (
                  <View style={styles.metaRow}><Text style={styles.metaLabel}>圖集</Text><Text style={styles.metaValue}>{String(rawData.rawMergedPayload.image_gallery.join(', '))}</Text></View>
                ) : null
              ) : null}
            </View>
          </View>
        ) : null}

        {tags && tags.length ? (
          <View style={styles.section}>
            <View style={styles.sectionDivider} />
            <View style={styles.tagsRow}>
              {tags.map((t) => (
                <View key={t} style={styles.tagPill}><Text style={styles.tagText}>{t}</Text></View>
              ))}
            </View>
          </View>
        ) : null}

        {rawData ? (
          <View style={[styles.section, { marginTop: 16 }]}>
            <View style={styles.sectionDivider} />
            <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 8 }}>調試原始資料</Text>
            <View style={styles.debugBlock}>
              {(Object.entries(rawData.rawMergedPayload || rawData) as [string, any][]).map(([k, v]) => (
                <View key={k} style={styles.debugRow}>
                  <Text style={styles.debugKey}>{k}</Text>
                  <Text style={styles.debugVal}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</Text>
                </View>
              ))}
            </View>
          </View>
        ) : null}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  headerImageWrapper: { width: '100%', aspectRatio: 16 / 9, backgroundColor: '#eee', position: 'relative', overflow: 'hidden', alignItems: 'center' },
  headerImage: { width: '100%', aspectRatio: 16 / 9, resizeMode: 'cover' },
  headerOverlay: { position: 'absolute', left: 0, right: 0, top: 0, height: '100%', backgroundColor: 'rgba(0,0,0,0.12)' },
  backButton: { position: 'absolute', left: 12, top: 16, width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: ORANGE_ACTION },
  webContentContainer: {
    alignSelf: 'center',
    width: 'var(--travel-content-w, 420px)' as any,
    maxWidth: '100%' as any,
  },
  content: { paddingHorizontal: 24, paddingTop: 14 },
  title: { fontSize: 26, fontWeight: '800', color: '#111827', marginVertical: 8 },
  addressRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  addressText: { marginLeft: 8, color: '#8E8E93', fontSize: 15 },
  section: { marginTop: 8, marginBottom: 8 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  sectionHeaderText: { marginLeft: 8, fontSize: 18, fontWeight: '800', color: '#333' },
  description: { color: '#8E8E93', lineHeight: 24, paddingLeft: 28 },
  sectionDivider: { height: 1, backgroundColor: '#E5E7EB', marginVertical: 12 },
  metaBlock: { paddingVertical: 6, paddingLeft: 28 },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10 },
  metaRowDivider: { borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  metaLabel: { color: '#8E8E93', fontSize: 15 },
  metaValue: { fontSize: 15, color: '#111', fontWeight: '700' },
  tagsRow: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 8, paddingLeft: 28 },
  tagPill: { backgroundColor: '#E6F0FF', paddingHorizontal: 14, paddingVertical: 6, borderRadius: 999, marginRight: 8, marginBottom: 8 },
  tagText: { color: '#0B61D6', fontWeight: '700' },
  debugBlock: { backgroundColor: '#FAFAFB', padding: 12, paddingLeft: 28, borderRadius: 8 },
  debugRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  debugKey: { color: '#6B7280', fontSize: 13, flex: 1 },
  debugVal: { color: '#111827', fontSize: 13, flex: 2, textAlign: 'right' },
  galleryThumb: { width: 104, height: 72, borderRadius: 8, marginRight: 10, backgroundColor: '#F3F4F6' },
});

export default PoiDetailContent;

// web-only stylesheet injection for header image sizing
if (typeof globalThis !== 'undefined' && (globalThis as any).document) {
  const doc: any = (globalThis as any).document;
  const id = 'cm-poi-header-img-style';
  if (!doc.getElementById(id)) {
    const s: any = doc.createElement('style');
    s.id = id;
    s.textContent = `
      .cm-poi-header-wrapper { width: 100%; display: flex; justify-content: center; }
      .cm-poi-image-shell { position: relative; width: var(--travel-content-w, 420px); max-width: calc(100% - 48px); }
      .cm-poi-header-img { display: block; width: 100%; height: auto; max-height: 60vh; object-fit: cover; border-top-left-radius: 0 !important; border-top-right-radius: 0 !important; }
      .cm-poi-back-btn { position: absolute; left: 12px; top: 12px; width: 40px; height: 40px; border-radius: 20px; background: rgba(255,255,255,0.12); border: none; color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; }
    `;
    doc.head.appendChild(s);
  }
}
